import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { Sellable } from '@spreadgroup/common-partner-lib/composition';
import type {
  SampleOrderItem,
  SelectableAppearance,
  SelectableSize,
} from './sampleOrderTypes';

/**
 * The public contract of the sample order journey — everything an entry point or a step component
 * may rely on.
 *
 * It is kept apart from the implementation on purpose: the follow-up tickets add entry points
 * (SMART product settings, dashboard, shop settings) and their only obligation is to supply a
 * composition. Nothing in this contract mentions publishing scope, composition mode or a route, so
 * an entry point that needs one of those is asking for something the journey does not offer.
 */

/**
 * How many different products a partner may put in one sample order.
 *
 * A real cap, not a page size: the selection step blocks further picks once it is reached, and the
 * counter reads `x/5` against it rather than against the number of products the composition has.
 * Five is the hard limit agreed in the UX meeting on 2026-08-27, replacing the four the designs
 * showed.
 *
 * The per-line quantity cap is separate and lives in `sampleOrderStock`, because it is clamped
 * against what is actually in stock.
 */
export const MAX_SAMPLE_PRODUCTS = 5;

export enum SampleOrderStep {
  /** Pick which of the composition's sellables to order. No prices and no stock in this step. */
  SELECTION = 'SELECTION',
  /** Choose colour, size and quantity per selected sellable, and see prices and the total. */
  CONFIGURATION = 'CONFIGURATION',
}

/** One configurable row of the configuration step, with everything that row needs to render. */
export interface SampleOrderLine {
  item: SampleOrderItem;
  appearances: SelectableAppearance[];
  sizes: SelectableSize[];
  unitPrice: number | null;
  lineTotal: number | null;
  maxQuantity: number;
  /** Nothing on this line is in stock, so no colour, size or quantity can be chosen. */
  unavailable: boolean;
  /**
   * The partner has not picked a size and has already tried to check out. Size is deliberately not
   * pre-selected — see `buildItem` — so this is the normal path, not an edge case.
   */
  sizeMissing: boolean;
  /**
   * False for the last remaining line. The configuration step has nothing to show without it, and a
   * small icon should not send the partner back a step — "Back" already does that.
   */
  canRemove: boolean;
  /** False when the print area count was assumed because the sellable carries no `printViewIds`. */
  printAreaCountCertain: boolean;
}

export interface SampleOrderJourney {
  step: Ref<SampleOrderStep>;
  loading: Ref<boolean>;
  submitting: Ref<boolean>;
  /** A translation key, or null. Never a raw backend message. */
  error: Ref<string | null>;
  sellables: ComputedRef<Sellable[]>;
  selectedCount: ComputedRef<number>;
  /**
   * The most products that can be selected here: everything the composition has, capped by
   * `MAX_SAMPLE_PRODUCTS`. The counter's denominator and what "select all" means.
   */
  selectableCount: ComputedRef<number>;
  /** The raw cap, for copy that names the limit itself. */
  selectionLimit: number;
  /** True once no further product can be added. */
  selectionLimitReached: ComputedRef<boolean>;
  /** False for a product that cannot be picked because the limit is already reached. */
  canSelect: (sellableId: string) => boolean;
  allSelected: ComputedRef<boolean>;
  lines: ComputedRef<SampleOrderLine[]>;
  total: ComputedRef<number | null>;
  currencyId: ComputedRef<number | string | undefined>;
  canContinue: ComputedRef<boolean>;
  canCheckout: ComputedRef<boolean>;
  /**
   * Sellables still missing a size, in list order. Empty means the order can be placed.
   */
  incompleteSellableIds: ComputedRef<string[]>;
  /**
   * Increments on every checkout attempt that was blocked by validation. A counter rather than a
   * flag so that clicking again with the same unresolved line scrolls to it again.
   */
  blockedCheckoutAttempts: Ref<number>;
  isSelected: (sellableId: string) => boolean;
  toggleSelection: (sellableId: string) => void;
  setAllSelected: (selected: boolean) => void;
  /**
   * Resolves once the configuration step is open, **rejects with `SampleOrderStepFailed`** if it
   * could not be reached. The CTA is a `ProgressButton`, which reads success from the promise.
   */
  goToConfiguration: () => Promise<void>;
  goToSelection: () => void;
  setAppearance: (sellableId: string, appearanceId: string) => void;
  setSize: (sellableId: string, sizeId: string) => void;
  setQuantity: (sellableId: string, quantity: number) => void;
  /** Drops a line, and deselects it so the selection step agrees. Refuses on the last line. */
  removeItem: (sellableId: string) => void;
  /**
   * Resolves once the partner is handed over to the checkout, **rejects with
   * `SampleOrderStepFailed`** otherwise — including when a line is still missing a size, where the
   * message goes on the line rather than into `error`. Resolving on a failure would show a checkmark
   * for an order that was never placed.
   */
  checkout: () => Promise<void>;
}

/** Step components read the journey from here rather than taking it apart into props. */
export const SAMPLE_ORDER_JOURNEY: InjectionKey<SampleOrderJourney> =
  Symbol('sampleOrderJourney');

/** Everything the partner is shown on failure is one of these keys. */
export const SAMPLE_ORDER_ERROR = {
  NO_SHOP: 'SAMPLE_ORDER.ERROR.NO_SHOP',
  LOAD_FAILED: 'SAMPLE_ORDER.ERROR.LOAD_FAILED',
  CHECKOUT_FAILED: 'SAMPLE_ORDER.ERROR.CHECKOUT_FAILED',
  SIZE_REQUIRED: 'SAMPLE_ORDER.CONFIGURATION.SIZE_REQUIRED',
  NO_CHECKOUT_LINK: 'SAMPLE_ORDER.ERROR.NO_CHECKOUT_LINK',
} as const;
